import React, { useState, useRef, useEffect, useCallback } from 'react'

const BeforeAfterSlider = ({ beforeSrc, afterSrc, containerRef, className = "" }) => {
  const [sliderPosition, setSliderPosition] = useState(50)
  const [isDragging, setIsDragging] = useState(false)
  const [imageBounds, setImageBounds] = useState(null)
  
  const sliderRef = useRef(null)
  const resizeObserverRef = useRef(null)

  // Optimized image bounds calculation
  const calculateImageBounds = useCallback(() => {
    const container = containerRef?.current
    if (!container) return
    
    const img = container.querySelector('.main-image')
    if (!img || !img.complete) return
    
    const imgWidth = img.clientWidth
    const imgHeight = img.clientHeight
    const containerWidth = container.clientWidth
    const containerHeight = container.clientHeight
    
    setImageBounds({
      x: (containerWidth - imgWidth) / 2,
      y: (containerHeight - imgHeight) / 2,
      width: imgWidth,
      height: imgHeight
    })
  }, [containerRef])

  // ResizeObserver for efficient size change detection
  useEffect(() => {
    const container = containerRef?.current
    if (!container) return

    const img = container.querySelector('.main-image')
    if (img && !img.complete) {
      img.onload = calculateImageBounds
    } else {
      calculateImageBounds()
    }

    resizeObserverRef.current = new ResizeObserver(calculateImageBounds)
    resizeObserverRef.current.observe(container)
    
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect()
      }
    }
  }, [containerRef, calculateImageBounds])

  // Optimized slider position update with bounds checking
  const updateSliderPosition = useCallback((e) => {
    if (!sliderRef.current || !imageBounds) return
    
    const rect = sliderRef.current.getBoundingClientRect()
    const clientX = e.touches ? e.touches[0].clientX : e.clientX
    const x = clientX - rect.left
    const imageX = x - imageBounds.x
    
    // Clamp percentage between 0-100
    const percentage = Math.max(0, Math.min(100, (imageX / imageBounds.width) * 100))
    setSliderPosition(percentage)
  }, [imageBounds])

  // Throttled mouse move for smooth performance
  const throttledMouseMove = useCallback((e) => {
    if (!isDragging) return
    requestAnimationFrame(() => updateSliderPosition(e))
  }, [isDragging, updateSliderPosition])

  const handleSliderMouseDown = useCallback((e) => {
    e.preventDefault()
    setIsDragging(true)
    updateSliderPosition(e)
  }, [updateSliderPosition])

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  // Event listeners with passive option for better performance
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', throttledMouseMove, { passive: true })
      document.addEventListener('mouseup', handleMouseUp)
      document.addEventListener('touchmove', throttledMouseMove, { passive: true })
      document.addEventListener('touchend', handleMouseUp)
    }

    return () => {
      document.removeEventListener('mousemove', throttledMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.removeEventListener('touchmove', throttledMouseMove)
      document.removeEventListener('touchend', handleMouseUp)
    }
  }, [isDragging, throttledMouseMove, handleMouseUp])

  // Don't render until bounds are calculated
  if (!imageBounds) return null

  // Optimized styles - pre-calculated for performance
  const overlayStyle = {
    position: 'absolute',
    top: imageBounds.y,
    left: imageBounds.x,
    width: imageBounds.width,
    height: imageBounds.height,
    overflow: 'hidden',
    pointerEvents: 'auto',
    zIndex: 10
  }

  const baseImageStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block'
  }

  const afterImageStyle = {
    ...baseImageStyle,
    clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`,
    zIndex: 2
  }

  const beforeImageStyle = {
    ...baseImageStyle,
    zIndex: 1
  }

  const handleStyle = {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: `${sliderPosition}%`,
    width: '2px',
    backgroundColor: '#007acc',
    zIndex: 10,
    transform: 'translateX(-50%)'
  }

  const handleButtonStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '40px',
    height: '40px',
    backgroundColor: '#007acc',
    border: '3px solid white',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: '16px',
    fontWeight: '600',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
    transform: 'translate(-50%, -50%)',
    cursor: 'col-resize',
    userSelect: 'none',
    transition: 'all 0.2s ease'
  }

  const labelsContainerStyle = {
    position: 'absolute',
    top: '16px',
    left: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0 16px',
    zIndex: 5,
    pointerEvents: 'none'
  }

  const labelStyle = {
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    color: 'white',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
    transition: 'all 0.2s ease'
  }

  return (
    <div 
      className={`before-after-slider ${className}`}
      ref={sliderRef}
      onMouseDown={handleSliderMouseDown}
      onTouchStart={handleSliderMouseDown}
      style={overlayStyle}
    >
      {/* Background image (edited version) */}
      <img
        src={afterSrc}
        alt="Before"
        style={beforeImageStyle}
      />
      
      {/* Overlay image (original version) with clipping */}
      <img
        src={beforeSrc}
        alt="After"
        style={afterImageStyle}
      />
      
      {/* Slider handle */}
      <div
        style={handleStyle}
        onMouseDown={handleSliderMouseDown}
        onTouchStart={handleSliderMouseDown}
      >
        <div style={handleButtonStyle}>
          ◀▶
        </div>
      </div>
      
      {/* Labels */}
      <div style={labelsContainerStyle}>
        <span 
          style={{
            ...labelStyle,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            borderColor: 'rgba(255, 255, 255, 0.15)'
          }}
        >
          Before
        </span>
        <span 
          style={{
            ...labelStyle,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            borderColor: 'rgba(255, 255, 255, 0.15)'
          }}
        >
          After
        </span>
      </div>
    </div>
  )
}

export default BeforeAfterSlider